import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Chip, IconButton, Tooltip, Collapse, Skeleton,
  TextField, InputAdornment, Paper, Divider, Button, MenuItem, Grid, Card
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import InboxIcon from '@mui/icons-material/Inbox';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SortIcon from '@mui/icons-material/Sort';
import FilterListIcon from '@mui/icons-material/FilterList';
import { fetchRecords } from '../services/api';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB'); // DD/MM/YYYY
};

const getMonthKey = (d) => {
  if (!d) return 'Unknown';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

// Sort function for MonthKeys (latest first)
const sortMonthKeys = (a, b) => {
  if (a === 'Unknown') return 1;
  if (b === 'Unknown') return -1;
  return new Date(b).getTime() - new Date(a).getTime();
};

export default function AttendanceGrouped({ records, onEdit, onDelete, onAdd, onView }) {
  const [expandedMonth, setExpandedMonth] = useState(null);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [search, setSearch] = useState('');
  
  // Filters
  const [filterMonth, setFilterMonth] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [filterBG, setFilterBG] = useState('');
  
  const [assignments, setAssignments] = useState([]);
  const [persons, setPersons] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [bgs, setBgs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchRecords('ASSIGNMENT').catch(() => ({ data: { data: [] } })),
      fetchRecords('PERSON').catch(() => ({ data: { data: [] } })),
      fetchRecords('COMPANY').catch(() => ({ data: { data: [] } })),
      fetchRecords('BUSINESS_GROUP').catch(() => ({ data: { data: [] } })),
    ]).then(([a, p, c, bg]) => {
      setAssignments(a.data.data || []);
      setPersons(p.data.data || []);
      setCompanies(c.data.data || []);
      setBgs(bg.data.data || []);
      setLoading(false);
    });
  }, []);

  const assignmentMap = useMemo(() => Object.fromEntries(assignments.map(a => [a.assignment_id, a])), [assignments]);
  const personMap = useMemo(() => Object.fromEntries(persons.map(p => [p.person_id, p])), [persons]);

  const monthOptions = useMemo(() => {
    const months = new Set();
    records.forEach(r => {
      if (r.attendance_date) months.add(getMonthKey(r.attendance_date));
    });
    return Array.from(months).sort(sortMonthKeys);
  }, [records]);

  // Expand the latest month by default once data is loaded
  useEffect(() => {
    if (!loading && monthOptions.length > 0 && !expandedMonth && !filterMonth) {
      setExpandedMonth(monthOptions[0]);
    }
  }, [loading, monthOptions, expandedMonth, filterMonth]);

  const toggleEmployee = (month, empKey) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [`${month}_${empKey}`]: !prev[`${month}_${empKey}`]
    }));
  };

  // Group Level 1: Month -> Level 2: Employee -> Level 3: Daily Records
  const monthGroups = useMemo(() => {
    const mGroup = {};
    
    // Group by month
    for (const r of records) {
      const mKey = getMonthKey(r.attendance_date);
      if (!mGroup[mKey]) mGroup[mKey] = [];
      mGroup[mKey].push(r);
    }
    
    // Structure into Month -> Employee mapping
    const structured = Object.entries(mGroup).map(([mKey, mRecords]) => {
      const eGroup = {};
      
      for (const r of mRecords) {
        const asnId = r.assignment_id || '—';
        const pId = r.person_id || '—';
        const eKey = `${pId}_${asnId}`;
        
        if (!eGroup[eKey]) {
          const asn = assignmentMap[asnId];
          const person = asn ? personMap[asn.person_id] : personMap[pId];
          const fullName = person ? [person.first_name, person.last_name].filter(Boolean).join(' ') : null;
          
          eGroup[eKey] = {
            eKey,
            person_id: pId,
            assignment_id: asnId,
            fullName: fullName || (asn ? asn.employee_code : 'Unknown Employee'),
            company_id: r.COMPANY_id,
            bg_id: r.bg_id,
            entries: [],
            stats: { present: 0, absent: 0, ot_hours: 0 }
          };
        }
        
        const entry = r;
        eGroup[eKey].entries.push(entry);
        
        // Calculate stats
        const status = (entry.attendance_status || '').toLowerCase();
        if (status.includes('present') || status.includes('half')) eGroup[eKey].stats.present += 1;
        else if (status.includes('absent') || status.includes('leave') || status.includes('lop')) eGroup[eKey].stats.absent += 1;
        
        if (entry.ot_hours) eGroup[eKey].stats.ot_hours += Number(entry.ot_hours);
      }
      
      const employees = Object.values(eGroup).sort((a, b) => a.fullName.localeCompare(b.fullName));
      
      // Calculate overall month stats
      let monthTotalPresent = 0, monthTotalAbsent = 0, monthTotalOT = 0;
      employees.forEach(e => {
        monthTotalPresent += e.stats.present;
        monthTotalAbsent += e.stats.absent;
        monthTotalOT += e.stats.ot_hours;
      });
      
      return {
        month: mKey,
        employees,
        stats: { activeEmployees: employees.length, present: monthTotalPresent, absent: monthTotalAbsent, ot_hours: monthTotalOT }
      };
    });
    
    return structured.sort((a, b) => sortMonthKeys(a.month, b.month));
  }, [records, assignmentMap, personMap]);

  // Apply Search & Filters
  const filteredMonths = useMemo(() => {
    let result = monthGroups;

    // Filter Level 1: Month
    if (filterMonth) result = result.filter(m => m.month === filterMonth);
    
    result = result.map(m => {
      let filteredEmps = m.employees;
      
      // Filter Level 2: Company / BG
      if (filterCompany) filteredEmps = filteredEmps.filter(e => e.company_id === filterCompany);
      if (filterBG) filteredEmps = filteredEmps.filter(e => e.bg_id === filterBG);
      
      // Search
      const q = search.trim().toLowerCase();
      if (q) {
        filteredEmps = filteredEmps.map(e => {
          const empHit = 
            e.person_id.toLowerCase().includes(q) ||
            e.assignment_id.toLowerCase().includes(q) ||
            e.fullName.toLowerCase().includes(q);
            
          if (empHit) return e;
          
          // Or if they have a specific date hit (e.g. searching "15-May")
          const matchingEntries = e.entries.filter(entry => 
            fmtDate(entry.attendance_date).toLowerCase().includes(q) ||
            (entry.attendance_status || '').toLowerCase().includes(q)
          );
          
          return matchingEntries.length ? { ...e, entries: matchingEntries } : null;
        }).filter(Boolean);
      }
      
      return { ...m, employees: filteredEmps };
    }).filter(m => m.employees.length > 0);

    return result;
  }, [monthGroups, filterMonth, filterCompany, filterBG, search]);

  const totalEmployees = filteredMonths.reduce((sum, m) => sum + m.employees.length, 0);

  if (loading) {
    return (
      <Box>
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} height={80} sx={{ mb: 2, borderRadius: 2 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        
        {/* Top Row: Search & Status */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search Person ID, Name, Assignment..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{
              width: { xs: '100%', sm: 380 },
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white', fontSize: 13, borderRadius: '10px',
                '& fieldset': { borderColor: '#E8EDF5', borderWidth: '1.5px' },
                '&:hover fieldset': { borderColor: '#bbf7d0' },
                '&.Mui-focused fieldset': { borderColor: '#22c55e', borderWidth: '2px' },
              },
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94A3B8', fontSize: 17 }} /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon sx={{ fontSize: 15 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Stack direction="row" spacing={1.5}>
            <Stack direction="row" alignItems="center" gap={0.6}
              sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#3B82F6' }} />
              <Typography fontSize={12} fontWeight={600} color="text.secondary">
                {filteredMonths.length} Months
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" gap={0.6}
              sx={{ px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: 'white', border: '1.5px solid #E8EDF5' }}>
              <Typography fontSize={12} fontWeight={600} color="text.secondary">
                {totalEmployees} Employees
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Bottom Row: Filters */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ alignItems: 'center' }}>
          <FilterListIcon sx={{ fontSize: 20, color: '#9CA3AF' }} />
          
          <TextField select size="small" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            sx={{ width: 150, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px' } }}
            InputProps={{ sx: { fontSize: 13 } }}>
            <MenuItem value="">All Pay Periods</MenuItem>
            {monthOptions.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          
          <TextField select size="small" value={filterCompany} onChange={e => setFilterCompany(e.target.value)}
            sx={{ width: 160, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: '8px' } }}
            InputProps={{ sx: { fontSize: 13 } }}>
            <MenuItem value="">All Companies</MenuItem>
            {companies.map(c => <MenuItem key={c.company_id} value={c.company_id}>{c.company_name}</MenuItem>)}
          </TextField>
        </Stack>
      </Box>

      {/* Month Level Sections */}
      <Stack spacing={3}>
        {filteredMonths.length === 0 ? (
          <Paper sx={{ py: 8, textAlign: 'center', bgcolor: 'white', border: '1.5px solid #E5E7EB', borderRadius: '12px' }}>
            <Stack alignItems="center" gap={1.5}>
              <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <InboxIcon sx={{ fontSize: 26, color: '#9CA3AF' }} />
              </Box>
              <Typography fontWeight={600} fontSize={14} color="#374151">No Attendance Records Found</Typography>
              <Typography fontSize={12.5} color="#9CA3AF">Please select another month or add attendance records.</Typography>
              {onAdd && (
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => onAdd()}
                  sx={{ mt: 1, borderRadius: '8px', color: '#16a34a', borderColor: '#22c55e', '&:hover': { bgcolor: '#f0fdf4' } }}>
                  Add Record
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          filteredMonths.map(m => {
            const isMonthExpanded = expandedMonth === m.month || filterMonth === m.month;
            
            return (
              <Box key={m.month} sx={{ 
                bgcolor: 'white', borderRadius: '12px', border: '1.5px solid #E5E7EB',
                boxShadow: isMonthExpanded ? '0 4px 12px rgba(0,0,0,0.03)' : 'none',
                overflow: 'hidden', transition: 'all 0.2s'
              }}>
                {/* Month Header */}
                <Box
                  onClick={() => setExpandedMonth(isMonthExpanded && !filterMonth ? null : m.month)}
                  sx={{
                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                    px: 3, py: 2, cursor: filterMonth ? 'default' : 'pointer',
                    bgcolor: isMonthExpanded ? '#F8FAFC' : 'white',
                    borderBottom: isMonthExpanded ? '1px solid #E5E7EB' : 'none',
                    '&:hover': { bgcolor: filterMonth ? '#F8FAFC' : '#F1F5F9' },
                  }}
                >
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    bgcolor: '#EFF6FF', color: '#3B82F6',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CalendarMonthIcon sx={{ fontSize: 22 }} />
                  </Box>
                  
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      <Typography fontSize={18} fontWeight={700} color="#0F172A">{m.month}</Typography>
                      <Chip label="Pay Period Block" size="small" sx={{ height: 20, fontSize: 10, bgcolor: '#F1F5F9', color: '#64748B', fontWeight: 600 }} />
                    </Stack>
                    <Typography fontSize={12} color="#64748B" mt={0.5}>Strictly isolated attendance logs for {m.month}</Typography>
                  </Box>

                  {/* Month KPIs */}
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F8FAFC', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                      <Typography fontSize={10} color="#64748B" fontWeight={600} textTransform="uppercase">Staff</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#0F172A">{m.stats.activeEmployees} Active</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#F0FDF4', borderRadius: '8px', border: '1px solid #DCFCE7' }}>
                      <Typography fontSize={10} color="#16A34A" fontWeight={600} textTransform="uppercase">Presents</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#15803D">{m.stats.present} Days</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#FEF2F2', borderRadius: '8px', border: '1px solid #FEE2E2' }}>
                      <Typography fontSize={10} color="#EF4444" fontWeight={600} textTransform="uppercase">Absents</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#B91C1C">{m.stats.absent} Days</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', px: 1.5, py: 0.5, bgcolor: '#FEFCE8', borderRadius: '8px', border: '1px solid #FEF08A' }}>
                      <Typography fontSize={10} color="#EAB308" fontWeight={600} textTransform="uppercase">OT Sum</Typography>
                      <Typography fontSize={13} fontWeight={700} color="#A16207">{m.stats.ot_hours} hrs</Typography>
                    </Box>
                  </Box>

                  {!filterMonth && (
                    <IconButton size="small" sx={{ ml: 1, color: '#3B82F6' }}>
                      {isMonthExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  )}
                </Box>

                {/* Employee Cards Inside Month */}
                <Collapse in={isMonthExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ p: 2, bgcolor: '#F8FAFC' }}>
                    <Stack spacing={2}>
                      {m.employees.map(emp => {
                        const isEmpExpanded = expandedEmployees[`${m.month}_${emp.eKey}`];
                        
                        return (
                          <Card key={emp.eKey} elevation={0} sx={{
                            border: '1.5px solid', borderColor: isEmpExpanded ? '#8B5CF6' : '#E2E8F0',
                            borderRadius: '12px', transition: 'all 0.2s',
                            boxShadow: isEmpExpanded ? '0 4px 12px rgba(139,92,246,0.08)' : '0 1px 2px rgba(0,0,0,0.02)'
                          }}>
                            {/* Employee Header */}
                            <Box
                              onClick={() => toggleEmployee(m.month, emp.eKey)}
                              sx={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
                                px: 2.5, py: 2, cursor: 'pointer',
                                bgcolor: 'white', borderRadius: '12px',
                                '&:hover': { bgcolor: '#F8FAFC' }
                              }}
                            >
                              <Box sx={{
                                width: 40, height: 40, borderRadius: '50%',
                                background: 'linear-gradient(135deg,#8B5CF6,#6366F1)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                              }}>
                                <Typography fontWeight={700} fontSize={14} color="white">
                                  {emp.fullName.substring(0, 2).toUpperCase()}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Stack direction="row" alignItems="center" gap={1.5} mb={0.5}>
                                  <Typography fontSize={15} fontWeight={700} color="#0F172A">{emp.fullName}</Typography>
                                  <Chip label={emp.person_id} size="small" sx={{ height: 20, fontSize: 11, fontFamily: 'monospace', bgcolor: '#F1F5F9', color: '#475569' }} />
                                  <Chip label={emp.assignment_id} size="small" sx={{ height: 20, fontSize: 11, fontFamily: 'monospace', bgcolor: '#EEF2FF', color: '#4F46E5' }} />
                                </Stack>
                                
                                <Stack direction="row" alignItems="center" gap={1.5}>
                                  <Typography fontSize={12} color="#64748B" fontWeight={500}>
                                    Pay Period KPIs:
                                  </Typography>
                                  <Chip label={`P: ${emp.stats.present}d`} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#F0FDF4', color: '#15803D' }} />
                                  <Chip label={`A: ${emp.stats.absent}d`} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#FEF2F2', color: '#B91C1C' }} />
                                  <Chip label={`OT: ${emp.stats.ot_hours}h`} size="small" sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: '#F8FAFC', color: '#334155', border: '1px solid #E2E8F0' }} />
                                </Stack>
                              </Box>

                              <IconButton size="small" sx={{ color: isEmpExpanded ? '#8B5CF6' : '#94A3B8' }}>
                                {isEmpExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </Box>

                            {/* Daily Attendance Records */}
                            <Collapse in={isEmpExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ borderTop: '1px solid #E2E8F0', bgcolor: 'white', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                                
                                {/* Table Header */}
                                <Box sx={{ display: 'flex', px: 3, py: 1.5, borderBottom: '1px solid #F1F5F9', bgcolor: '#F8FAFC' }}>
                                  <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100 }}>DATE</Typography>
                                  <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ flex: 1 }}>STATUS</Typography>
                                  <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 80 }}>IN CLOCK</Typography>
                                  <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 80 }}>OUT CLOCK</Typography>
                                  <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 90, textAlign: 'center' }}>ACTUAL HRS</Typography>
                                  <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 80, textAlign: 'center' }}>OT HRS</Typography>
                                  <Typography fontSize={11} fontWeight={700} color="#64748B" sx={{ width: 100, textAlign: 'center' }}>ACTIONS</Typography>
                                </Box>

                                {/* Table Rows */}
                                {emp.entries.sort((a,b) => new Date(a.attendance_date).getTime() - new Date(b.attendance_date).getTime()).map((entry, idx) => {
                                  const isPresent = (entry.attendance_status || '').toLowerCase().includes('present');
                                  const isAbsent = (entry.attendance_status || '').toLowerCase().includes('absent');
                                  
                                  return (
                                    <Box key={entry.attendance_id || idx} sx={{ 
                                      display: 'flex', alignItems: 'center', px: 3, py: 1.5, 
                                      borderBottom: idx === emp.entries.length - 1 ? 'none' : '1px solid #F1F5F9',
                                      '&:hover': { bgcolor: '#F8FAFC' }
                                    }}>
                                      <Typography fontSize={13} fontWeight={600} color="#0F172A" sx={{ width: 100 }}>
                                        {fmtDate(entry.attendance_date)}
                                      </Typography>
                                      
                                      <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" alignItems="center" gap={0.5}>
                                          <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isPresent ? '#22C55E' : isAbsent ? '#EF4444' : '#F59E0B' }} />
                                          <Typography fontSize={12} fontWeight={600} color={isPresent ? '#15803D' : isAbsent ? '#B91C1C' : '#B45309'}>
                                            {entry.attendance_status || '—'}
                                          </Typography>
                                        </Stack>
                                      </Box>
                                      
                                      <Typography fontSize={13} color="#475569" sx={{ width: 80, fontFamily: 'monospace' }}>{entry.in_time || '—'}</Typography>
                                      <Typography fontSize={13} color="#475569" sx={{ width: 80, fontFamily: 'monospace' }}>{entry.out_time || '—'}</Typography>
                                      
                                      <Typography fontSize={13} fontWeight={600} color="#0F172A" sx={{ width: 90, textAlign: 'center' }}>
                                        {entry.actual_hours ? `${entry.actual_hours} hrs` : '0 hrs'}
                                      </Typography>
                                      
                                      <Typography fontSize={13} fontWeight={700} color={entry.ot_hours ? '#EAB308' : '#94A3B8'} sx={{ width: 80, textAlign: 'center' }}>
                                        {entry.ot_hours ? `+${entry.ot_hours} hrs` : '—'}
                                      </Typography>

                                      <Stack direction="row" spacing={0.5} sx={{ width: 100, justifyContent: 'center' }}>
                                        {onView && (
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onView(entry); }} sx={{ color: '#64748B', bgcolor: '#F1F5F9', '&:hover': { bgcolor: '#E2E8F0' } }}>
                                            <VisibilityIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        )}
                                        {onEdit && (
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(entry); }} sx={{ color: '#8B5CF6', bgcolor: '#F5F3FF', '&:hover': { bgcolor: '#EDE9FE' } }}>
                                            <EditIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        )}
                                        {onDelete && (
                                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(entry); }} sx={{ color: '#EF4444', bgcolor: '#FEF2F2', '&:hover': { bgcolor: '#FEE2E2' } }}>
                                            <DeleteIcon sx={{ fontSize: 14 }} />
                                          </IconButton>
                                        )}
                                      </Stack>
                                    </Box>
                                  );
                                })}
                              </Box>
                            </Collapse>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                </Collapse>
              </Box>
            );
          })
        )}
      </Stack>
    </Box>
  );
}
